import { useCallback, useEffect, useRef, useState } from 'react';

import { MockCertificate } from './mock-certificate';
import { Certificate } from './types';
import { CanvasRenderer } from './canvas';
import * as Signal from '@/utils/event-emitter';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import Text from '@ui/text/text/text';

type CanvasComponentProps = {
  certificate?: Certificate;
  animate?: boolean;
  showDebug?: boolean;
};

export default function CanvasComponent({ certificate: certificateProp, animate, showDebug }: CanvasComponentProps) {
  const [certificate, setCertificate] = useState<Certificate>(certificateProp ?? MockCertificate);
  const [ready, setReady] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderer = useRef<CanvasRenderer | null>(null);
  const certificateRef = useRef<Certificate>(certificate);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  const setCanvasRef = useCallback((node: HTMLCanvasElement | null) => {
    canvasRef.current = node;
  }, []);

  useEffect(() => {
    const initializeCanvas = async () => {
      if (fontsLoaded && canvasRef.current && !renderer.current) {
        renderer.current = new CanvasRenderer(certificate, 680, 960);
        await renderer.current.init(canvasRef.current);
        renderer.current.draw();
        setReady(true);
      }
    };

    initializeCanvas();

    return () => {
      if (renderer.current) {
        renderer.current = null;
      }
    };
  }, [certificate, fontsLoaded]);

  useEffect(() => {
    // canvas fonts must be fully loaded before attempting to render the canvas
    // otherwise the canvas will render the text in times new roman
    setFontsLoaded(false);
    const fonts = certificate.settings.fonts;
    const fontFiles = [];

    if (fonts.default) {
      fontFiles.push(new FontFace(fonts.default, `url(${fonts.default_url})`));
    }

    if (fonts.userName) {
      fontFiles.push(new FontFace(fonts.userName.font, `url(${fonts.userName.url})`));
    }

    if (fonts.dateString) {
      fontFiles.push(new FontFace(fonts.dateString.font, `url(${fonts.dateString.url})`));
    }

    if (fonts.certificateName) {
      fontFiles.push(new FontFace(fonts.certificateName.font, `url(${fonts.certificateName.url})`));
    }

    if (fonts.credits) {
      fontFiles.push(new FontFace(fonts.credits.font, `url(${fonts.credits.url})`));
    }

    if (fonts.categories) {
      fontFiles.push(new FontFace(fonts.categories.font, `url(${fonts.categories.url})`));
    }

    fontFiles.forEach((font) => {
      document.fonts.add(font);
    });

    const fontPromises = fontFiles.map((font) => font.load());

    Promise.all(fontPromises)
      .catch((e) => {
        console.error(`Error loading fonts: ${e}`);
      })
      .finally(() => {
        setFontsLoaded(true);
      });
  }, [certificate.settings.fonts]);

  useEffect(() => {
    Signal.on('download-certificate', (filename: string) => {
      renderer.current?.exportToPdf(filename);
    });

    return () => {
      Signal.off('download-certificate');
    };
  }, []);

  useEffect(() => {
    if (certificateProp && renderer.current) {
      if (certificateProp.settings.id !== certificateRef.current.settings.id) {
        renderer.current.updateCertificateDesign(certificateProp);
      } else if (
        certificateProp.settings.icon &&
        certificateRef.current.settings.icon !== certificateProp.settings.icon
      ) {
        renderer.current.updateIcon(certificateProp.settings.icon);
      }

      setCertificate(certificateProp);
      certificateRef.current = certificateProp;
    }
  }, [certificateProp]);

  useEffect(() => {
    let frame: number;

    const render = () => {
      renderer.current?.draw();

      if (animate) {
        frame = window.requestAnimationFrame(render);
      }
    };

    if (ready && renderer.current) {
      frame = window.requestAnimationFrame(render);
    }

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [ready, animate]);

  return (
    <>
      {fontsLoaded ? (
        <canvas
          ref={setCanvasRef}
          height={680}
          width={960}
          style={{
            maxWidth: '100%',
          }}
        />
      ) : (
        <WaitingIndicator />
      )}
      {showDebug && (
        <div>
          <Text className="H2" color="var(--gray-100)">
            Change these below
          </Text>
          <input
            style={{ width: '100%', fontSize: 14, padding: 5 }}
            type="text"
            value={certificate.userName}
            onChange={(e) => {
              const value = e.target.value;
              setCertificate({ ...certificate, userName: value });
              if (renderer.current) {
                renderer.current.userName.text = value;
              }
            }}
          />

          <input
            style={{ width: '100%', fontSize: 14, padding: 5 }}
            type="text"
            value={certificate.certificateName}
            onChange={(e) => {
              const value = e.target.value;
              setCertificate({ ...certificate, certificateName: value });
              if (renderer.current) {
                renderer.current.certificateName.text = value;
              }
            }}
          />
        </div>
      )}
    </>
  );
}
